/**
 * Standalone browser entry, bundled to `dist/file.js` (IIFE, global `AntispamFP`).
 *
 * Zero-JS integration — одна строка, больше ничего:
 *   <script src="https://cdn.spamowl.icu/file.js?id=<userId>"></script>
 *
 * На загрузке скрипт собирает отпечаток и POST'ит его коллектору — это клик, пишется
 * сервером на КАЖДОМ визите. При срабатывании ручного блока диспатчится `antispam:action`
 * (redirect / block / hard_mode). Авто-вердикт dormant — см. `@antispam/contracts` `verdict-flow.ts`.
 * URL коллектора зашит в бандл на сборке (`__COLLECT_ENDPOINT__`):
 * prod → collect.spamowl.icu, stage (ENV=stage) → collect-staging, dev → localhost.
 *
 * Кроме того SDK САМ обвязывает формы (form-интеграция) — странице свой JS не нужен:
 * проставляет hidden-поля `visitor_id` / `antispam_action` из вердикта; на `hard_mode` рисует
 * капчу (hCaptcha, sitekey зашит в бандл) прямо на форме и придерживает сабмит до её решения.
 * Все РЕШЕНИЯ по вердикту (постбек, роутинг в CPA, проверка токена капчи, фейковый успех для
 * block) делает сервер, читая hidden-поле `antispam_action`.
 *
 * Читается с тега:
 *   ?id=<userId>          id арендатора из URL скрипта (обязателен — лицензирование)
 *   data-subid="{subid}"  id клика трекера (опционально; иначе collect() детектит из URL).
 *                         Durable-ключ связки клик↔рега — бэкенд арба матчит постбек по нему.
 *   data-fail-open="0"    без вердикта форму НЕ отправлять (по умолчанию — отправлять)
 *   data-form="<sel>"     CSS-селектор форм (id/class/атрибут/обёртка) — переопределяет выбор форм
 *   data-hcaptcha-sitekey свой публичный sitekey hCaptcha (иначе — зашитый дефолт)
 *
 * Выбор форм (по приоритету): 1) data-form="<sel>" с тега; 2) помеченные <form data-so-form>;
 * 3) все <form>. В data-form можно указать и обёртку (напр. `#reg-form` на div) — возьмём <form>
 * внутри неё. Маркер/селектор проставляются при генерации лендинга.
 *
 * Доступ со страницы: window.AntispamFP.ready() / .result / .payload / .wireForms().
 * Fail-open: заблокированный SDK / недоступный API / таймаут никогда не бросают (result → null).
 */
import { collect, SDK_VERSION } from "@antispam/fingerprint";
import type { CollectOptions, CollectPayload } from "@antispam/fingerprint";

/** URL коллектора зашивается на сборке (tsup define): prod → spamowl, dev → localhost. */
declare const __COLLECT_ENDPOINT__: string;
// Фолбэк на прод-URL, если define не применился (не-tsup сборка) — чтобы бандл не падал
// ReferenceError на загрузке. `typeof` безопасен для необъявленного идентификатора.
const COLLECT_ENDPOINT =
  typeof __COLLECT_ENDPOINT__ !== "undefined"
    ? __COLLECT_ENDPOINT__
    : "https://collect.spamowl.icu/collect";

/** Бюджет ожидания сбора + вердикта, мс. */
const COLLECT_TIMEOUT_MS = 2500;

/** Действие ручного блока из ответа /collect (зеркалит BlockAction контракта). */
interface BlockAction {
  type: string; // 'block' | 'redirect' | 'hard_mode'
  redirectUrl?: string;
}
/** Debug-инфо лицензии из /collect (опционально; старые коллекторы поля не шлют). */
interface CollectLicenseInfo {
  status: "grace" | "expired";
  reason: "quota_exceeded" | "period_expired";
  message: string;
  graceUntil?: string;
}

/** Ответ нашего анализа (зеркалит CollectResponse; контракт в браузер не тянем). */
interface AnalysisResult {
  visitorId: string;
  action: BlockAction | null;
  license?: CollectLicenseInfo;
}

function findSelfScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) return document.currentScript;
  // Fallback для legacy-браузеров без document.currentScript: наш тег несёт `?id=` в src
  // (data-subid — опционально, тоже маркер).
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i]!;
    if (s.hasAttribute("data-subid") || hasIdParam(s.src)) return s;
  }
  return null;
}

/** Есть ли в src скрипта `?id=` — маркер нашего тега. */
function hasIdParam(src: string): boolean {
  try {
    return new URL(src).searchParams.has("id");
  } catch {
    return false;
  }
}

// Captured once, synchronously, while the tag is the current script — so the
// integration works under any filename (a.js, vendor.js, …), not just file.js.
const selfScript = findSelfScript();

function readUserId(script: HTMLScriptElement | null): string | undefined {
  if (!script) return undefined;
  try {
    return new URL(script.src).searchParams.get("id") ?? undefined;
  } catch {
    return undefined;
  }
}

const USER_ID = readUserId(selfScript);

/** Явный id клика трекера (`data-subid="{subid}"`); иначе collect() детектит из URL. */
function readSubId(script: HTMLScriptElement | null): string | undefined {
  return script?.getAttribute("data-subid") ?? undefined;
}

const SUBID = readSubId(selfScript);

/** Дефолтный публичный sitekey капчи — ОДИН на всех тенантов (зашит в бандл). */
const HCAPTCHA_SITEKEY_DEFAULT = "aaa09ca6-552c-4188-90ec-d7729a6c92d8";
/**
 * Публичный sitekey капчи: `data-hcaptcha-sitekey="..."` с тега переопределяет дефолт —
 * тенант может подключить свою hCaptcha (парный secret ставится на сервере). Пусто → дефолт.
 */
function readHcaptchaSitekey(script: HTMLScriptElement | null): string {
  const s = script?.getAttribute("data-hcaptcha-sitekey");
  return s && s.trim() ? s.trim() : HCAPTCHA_SITEKEY_DEFAULT;
}
const HCAPTCHA_SITEKEY = readHcaptchaSitekey(selfScript);
/** Скрипт hCaptcha (explicit-render + единый onload-колбэк). */
const HCAPTCHA_SRC = "https://js.hcaptcha.com/1/api.js?render=explicit&onload=__soHcReady";

/**
 * Fail-open с тега: `data-fail-open="0"` → без вердикта форму НЕ отправлять; иначе (по умолчанию)
 * отправлять как обычно.
 */
function readFailOpen(script: HTMLScriptElement | null): boolean {
  return script?.getAttribute("data-fail-open") !== "0";
}
const FAIL_OPEN = readFailOpen(selfScript);

/**
 * Кастомный CSS-селектор форм с тега: `data-form="#reg-form"` / `.reg` / `[data-my-form]`.
 * Переопределяет выбор форм. Можно указать и обёртку — тогда возьмём <form> внутри неё.
 */
function readFormSelector(script: HTMLScriptElement | null): string | null {
  const s = script?.getAttribute("data-form");
  return s && s.trim() ? s.trim() : null;
}
const FORM_SELECTOR = readFormSelector(selfScript);

let lastPayload: CollectPayload | null = null;
let inFlight: Promise<CollectPayload> | null = null;

/** Кэш единственного клика на визит: ровно один /collect за загрузку. */
let analysisInFlight: Promise<AnalysisResult | null> | null = null;
/** Результат визит-клика (вердикт + visitorId) — читается страницей для постбеков. */
let lastResult: AnalysisResult | null = null;

async function run(options: CollectOptions = {}): Promise<CollectPayload> {
  const payload = await collect({ userId: USER_ID, trackerClickId: SUBID, ...options });
  lastPayload = payload;
  try {
    window.dispatchEvent(new CustomEvent("antispam:fingerprint", { detail: payload }));
  } catch {
    /* CustomEvent unsupported — payload still available via api.payload */
  }
  return payload;
}

/** Collect once and reuse: дедуп сбора отпечатка между визит-кликом и ручными вызовами. */
function ensurePayload(options?: CollectOptions): Promise<CollectPayload> {
  if (lastPayload) return Promise.resolve(lastPayload);
  if (!inFlight) {
    inFlight = run(options).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * POST payload коллектору, вернуть вердикт. Сервер мапит payload (`toCollectRequest`,
 * ip/UA из заголовков), скорит, отдаёт CollectResponse. `keepalive` — пережить навигацию.
 * Сбой / non-2xx / таймаут → null (fail-open).
 */
function postVerdict(payload: CollectPayload, ms: number): Promise<AnalysisResult | null> {
  if (typeof fetch !== "function") return Promise.resolve(null);
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => ctrl?.abort(), ms);
  return fetch(COLLECT_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId: USER_ID, payload }),
    signal: ctrl?.signal,
    keepalive: true,
    credentials: "omit",
  })
    .then((r) => (r.ok ? (r.json() as Promise<AnalysisResult>) : null))
    .catch(() => null)
    .finally(() => clearTimeout(timer));
}

/** Collect → analyze, bounded by `ms`. Resolves to the verdict or null (fail-open). */
function analyze(ms = COLLECT_TIMEOUT_MS): Promise<AnalysisResult | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: AnalysisResult | null) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    const timer = setTimeout(() => done(null), ms);
    ensurePayload().then(
      (payload) =>
        postVerdict(payload, ms).then((result) => {
          clearTimeout(timer);
          done(result);
        }),
      () => {
        clearTimeout(timer);
        done(null);
      },
    );
  });
}

/**
 * Применить действие ручного блока на визите: `redirect` уводит сразу; `block`/`hard_mode` —
 * диспатч события `antispam:action` (страница реагирует: капча / fake-success). null — пропуск.
 */
function applyAction(result: AnalysisResult | null): void {
  const action = result?.action ?? null;
  if (action && action.type === "redirect" && action.redirectUrl) {
    try {
      window.location.replace(action.redirectUrl);
      return;
    } catch {
      /* навигация недоступна — падаем в событие ниже */
    }
  }
  try {
    window.dispatchEvent(new CustomEvent("antispam:action", { detail: action }));
  } catch {
    /* CustomEvent unsupported */
  }
}

/** Единый клик на визит: ровно один /collect за загрузку, действие применяется один раз. */
function ensureAnalysis(): Promise<AnalysisResult | null> {
  if (!analysisInFlight) {
    analysisInFlight = analyze().then((result) => {
      lastResult = result;
      applyAction(result);
      return result;
    });
  }
  return analysisInFlight;
}

// ─────────────────────────────────────────────────────────────────────────────
// Форм-интеграция. SDK сам: заполняет hidden-поля из вердикта, на `hard_mode` рисует
// капчу прямо на форме и придерживает сабмит до её решения. Решения по вердикту (постбек,
// роутинг в CPA, проверка токена, фейковый успех для block) — на сервере по `antispam_action`.
// ─────────────────────────────────────────────────────────────────────────────

/** Тип действия ручного блока из CollectResponse (`action.type`). */
function verdictType(result: AnalysisResult | null): string | null {
  return result?.action?.type ?? null;
}

/** hCaptcha грузится один раз на страницу; резолвится, когда `window.hcaptcha` готов. */
let hcaptchaLoad: Promise<void> | null = null;
function loadHcaptcha(): Promise<void> {
  if (window.hcaptcha && typeof window.hcaptcha.render === "function") return Promise.resolve();
  if (hcaptchaLoad) return hcaptchaLoad;
  hcaptchaLoad = new Promise<void>((resolve) => {
    window.__soHcReady = () => resolve();
    const s = document.createElement("script");
    s.src = HCAPTCHA_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  });
  return hcaptchaLoad;
}

/** Найти hidden-поле по имени или создать и добавить в форму. */
function ensureHidden(form: HTMLFormElement, name: string): HTMLInputElement {
  let el = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
  if (!el) {
    el = document.createElement("input");
    el.type = "hidden";
    el.name = name;
    form.appendChild(el);
  }
  return el;
}

/** Контейнер под капчу: существующий `[data-so-captcha]` или новый div перед кнопкой сабмита. */
function ensureCaptchaBox(form: HTMLFormElement): HTMLElement {
  let box = form.querySelector<HTMLElement>("[data-so-captcha]");
  if (!box) {
    box = document.createElement("div");
    box.setAttribute("data-so-captcha", "");
    const btn = form.querySelector<HTMLElement>('[type="submit"], button');
    if (btn && btn.parentNode) btn.parentNode.insertBefore(box, btn);
    else form.appendChild(box);
  }
  return box;
}

/** querySelectorAll, но не бросает на кривом селекторе (вернёт пусто). */
function qsa(selector: string): Element[] {
  const out: Element[] = [];
  try {
    document.querySelectorAll(selector).forEach((el) => out.push(el));
  } catch {
    /* невалидный селектор — молча пусто */
  }
  return out;
}

/** Элемент → сам <form>, либо <form> внутри (селектор мог указывать на обёртку). */
function toForm(el: Element): HTMLFormElement | null {
  return el instanceof HTMLFormElement ? el : el.querySelector<HTMLFormElement>("form");
}

/**
 * Формы для интеграции по приоритету:
 *   1) data-form="<sel>" с тега (id/class/атрибут/обёртка);
 *   2) помеченные `form[data-so-form]`;
 *   3) все `form`.
 */
function targetForms(): HTMLFormElement[] {
  const selector = FORM_SELECTOR ?? (qsa("form[data-so-form]").length ? "form[data-so-form]" : "form");
  const out: HTMLFormElement[] = [];
  qsa(selector).forEach((el) => {
    const form = toForm(el);
    if (form && out.indexOf(form) === -1) out.push(form);
  });
  return out;
}

/** Обвязать одну форму: hidden-поля, капча на `hard_mode`, гейт сабмита. Идемпотентно. */
function wireForm(form: HTMLFormElement): void {
  if (form.dataset.soWired) return; // не навешиваться дважды
  form.dataset.soWired = "1";

  const fVisitor = ensureHidden(form, "visitor_id");
  const fAction = ensureHidden(form, "antispam_action");
  let rendered = false;
  let token = "";
  let pending: (() => void) | null = null;
  let busy = false;

  const fill = (result: AnalysisResult | null): void => {
    if (!result) return;
    fVisitor.value = result.visitorId || "";
    fAction.value = result.action?.type ?? "";
  };

  const renderCaptcha = (): void => {
    if (rendered || !HCAPTCHA_SITEKEY) return;
    rendered = true;
    const box = ensureCaptchaBox(form);
    void loadHcaptcha().then(() => {
      try {
        window.hcaptcha!.render(box, {
          sitekey: HCAPTCHA_SITEKEY,
          callback: (t: string) => {
            token = t || "";
            if (pending) {
              const fn = pending;
              pending = null;
              fn();
            }
          },
          "expired-callback": () => {
            token = "";
          },
          "error-callback": () => {
            token = "";
          },
        });
      } catch {
        /* виджет упал — не блокируем форму */
      }
    });
  };

  const onVerdict = (result: AnalysisResult | null): void => {
    fill(result);
    if (verdictType(result) === "hard_mode") renderCaptcha(); // капча сразу, до сабмита
  };

  void ensureAnalysis().then(onVerdict, () => {});

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (busy) return;
    busy = true;

    const proceed = (result: AnalysisResult | null): void => {
      fill(result);
      const type = verdictType(result);

      // Ответа нет → fail-open с тега
      if (!result) {
        if (FAIL_OPEN) form.submit();
        else busy = false;
        return;
      }

      // redirect → уводим, форму не шлём
      if (type === "redirect" && result.action?.redirectUrl) {
        try {
          window.location.replace(result.action.redirectUrl);
        } catch {
          /* навигация недоступна */
        }
        busy = false;
        return;
      }

      // hard_mode → нужна решённая капча
      if (type === "hard_mode" && HCAPTCHA_SITEKEY) {
        renderCaptcha();
        if (token) form.submit();
        else {
          pending = () => form.submit();
          busy = false;
        }
        return;
      }

      // block / чисто / прочее → сабмит; решение принимает сервер по antispam_action
      form.submit();
    };

    void ensureAnalysis().then(proceed, () => proceed(null));
  });
}

/** Обвязать все целевые формы. Публично — для форм, добавленных в DOM после загрузки. */
function wireForms(): void {
  targetForms().forEach(wireForm);
}

function initForms(): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wireForms, { once: true });
  } else {
    wireForms();
  }
}

const api = {
  version: SDK_VERSION,
  collect: run,
  /**
   * Промис визит-клика: резолвится вердиктом + `visitorId` (переиспользует единственный
   * /collect визита, второго запроса не шлёт). Так бэк арба получает visitorId и накатывает
   * по нему постбеки: `AntispamFP.ready().then(r => { if (r) usePostback(r.visitorId); })`.
   * На fail-open (таймаут/сбой) резолвится в `null` — обязательно проверяй.
   */
  ready: ensureAnalysis,
  /** Ручная обвязка форм — для форм, добавленных в DOM уже после загрузки страницы. */
  wireForms,
  get payload(): CollectPayload | null {
    return lastPayload;
  },
  /** Результат визит-клика (вердикт + `visitorId`), когда уже готов; иначе null. */
  get result(): AnalysisResult | null {
    return lastResult;
  },
};

// Клик на визите + авто-обвязка форм: как только загрузился наш тег.
if (selfScript) {
  void ensureAnalysis();
  initForms();
}

// Экспонируем API как `window.AntispamFP` напрямую — без ESM-обёртки `.default`,
// чтобы `AntispamFP.ready()` / `AntispamFP.collect()` работали из любого скрипта на странице.
declare global {
  interface Window {
    AntispamFP: typeof api;
    hcaptcha?: { render: (el: HTMLElement, opts: Record<string, unknown>) => unknown; reset?: (id?: unknown) => void };
    __soHcReady?: () => void;
  }
}
if (typeof window !== "undefined") {
  window.AntispamFP = api;
}
